import { useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUser, useLogout } from "@/stores/auth.store";
import { useThemeStore } from "@/stores/theme.store";
import { useToast } from "@/hooks/use-toast";
import { Menu, User, LogOut, ChevronDown, Sun, Moon, Bell } from "lucide-react";
import { NotificationDropdown } from "@/components/notifications/notification-dropdown";
import { fetchUnreadCount } from "@/services/notification.service";
import { apiService } from "@/services/api.service";

interface HeaderProps {
  setSidebarOpen: (open: boolean) => void;
  sidebarOpen?: boolean;
}

export default function Header({ setSidebarOpen, sidebarOpen = false }: HeaderProps) {
  const user = useUser();
  const logout = useLogout();
  const { toast } = useToast();
  const { resolvedTheme, toggleTheme } = useThemeStore();
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const loadUnreadCount = useCallback(async () => {
    const res = await fetchUnreadCount();
    if (res.ok && res.data) {
      setUnreadCount(res.data.count);
    }
  }, []);

  useEffect(() => {
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 30_000);
    return () => clearInterval(interval);
  }, [loadUnreadCount]);

  const handleLogout = () => {
    logout(toast);
  };

  // Busca o nome da empresa do user pra exibir contexto multi-empresa no header (CAAS-4249)
  const companyId = user?.companyId ?? null;
  const { data: companyData } = useQuery<{ id: string; name: string } | null>({
    queryKey: ["company", companyId],
    queryFn: async () => {
      if (!companyId) return null;
      const res = await apiService.get<{ id: string; name: string }>(`/companies/${companyId}`);
      return res.ok && res.data ? res.data : null;
    },
    enabled: Boolean(companyId),
    staleTime: 5 * 60 * 1000, // 5min — empresa muda pouco
  });
  const companyName = companyData?.name ?? "";

  const initials =
    user?.name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";

  return (
    <header className="bg-header border-b border-border px-6 py-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          {/* Mobile menu button — CAAS-4312: aria-controls aponta pro nav landmark */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden mr-3 h-7 w-7"
            aria-controls="primary-nav"
            aria-expanded={sidebarOpen}
            aria-label={sidebarOpen ? "Fechar menu" : "Abrir menu"}
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <Menu className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>

        <div className="flex items-center space-x-3">
          {/* Theme Toggle */}
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={toggleTheme}>
            {resolvedTheme === "dark" ? (
              <Sun className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Moon className="h-4 w-4 text-muted-foreground" />
            )}
            <span className="sr-only">Alternar Tema</span>
          </Button>

          {/* Notifications Bell */}
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 relative"
              data-notification-bell
              aria-label={
                unreadCount > 0
                  ? `Notificações: ${unreadCount > 99 ? "99+" : unreadCount} não lidas`
                  : "Notificações"
              }
              onClick={() => setNotifOpen((prev) => !prev)}
            >
              <Bell className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              {unreadCount > 0 && (
                <span
                  aria-hidden="true"
                  className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white"
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </Button>
            <NotificationDropdown
              open={notifOpen}
              onClose={() => setNotifOpen(false)}
              onCountChange={loadUnreadCount}
            />
          </div>

          {/* User Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center space-x-2 h-auto py-1.5">
                <div
                  aria-hidden="true"
                  className="w-8 h-8 bg-gradient-to-br from-primary to-sidebar-primary rounded-full flex items-center justify-center shadow-md"
                >
                  <span aria-hidden="true" className="text-xs font-bold text-white">
                    {initials}
                  </span>
                </div>
                <div className="hidden md:flex flex-col items-start leading-tight text-left">
                  <span className="text-sm font-semibold text-foreground">
                    {user?.name || user?.email}
                  </span>
                  {companyName && (
                    <span
                      className="text-[11px] font-normal text-muted-foreground"
                      data-testid="header-company-name"
                    >
                      {companyName}
                    </span>
                  )}
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem className="cursor-pointer">
                <User className="mr-2 h-4 w-4" />
                Perfil
              </DropdownMenuItem>
              {(user as any)?.isGoogle && (
                <div className="px-2 py-1.5 flex items-center gap-2 text-xs text-muted-foreground">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Google vinculado
                </div>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="cursor-pointer text-destructive focus:text-destructive"
                onClick={handleLogout}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
